import React, { useState, useRef, Fragment, createElement } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { MaternityFile } from '../../types';
import { Search, FileText, Printer, Download, Baby, X, Trash2 } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
async function captureToA4Pdf(element: HTMLElement, filename: string) {
  const PAGE_W_MM = 210;
  const PAGE_H_MM = 297;
  const MARGIN_TOP_MM = 20;
  const MARGIN_BOTTOM_MM = 20;
  const MARGIN_LEFT_MM = 18;
  const MARGIN_RIGHT_MM = 18;
  const CONTENT_W_MM = PAGE_W_MM - MARGIN_LEFT_MM - MARGIN_RIGHT_MM;
  const CONTENT_H_MM = PAGE_H_MM - MARGIN_TOP_MM - MARGIN_BOTTOM_MM;
  const sourceCanvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    backgroundColor: '#ffffff',
    logging: false
  });
  const pdf = new jsPDF({
    unit: 'mm',
    format: 'a4',
    orientation: 'portrait',
    compress: true
  });
  const sourceW = sourceCanvas.width;
  const sourceH = sourceCanvas.height;
  const pxPerMm = sourceW / CONTENT_W_MM;
  const pageSlicePx = Math.floor(CONTENT_H_MM * pxPerMm);
  let cursorPx = 0;
  let pageIndex = 0;
  while (cursorPx < sourceH) {
    const remainingPx = sourceH - cursorPx;
    const sliceHpx = Math.min(pageSlicePx, remainingPx);
    const sliceCanvas = document.createElement('canvas');
    sliceCanvas.width = sourceW;
    sliceCanvas.height = sliceHpx;
    const ctx = sliceCanvas.getContext('2d');
    if (!ctx) break;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, sourceW, sliceHpx);
    ctx.drawImage(sourceCanvas, 0, cursorPx, sourceW, sliceHpx, 0, 0, sourceW, sliceHpx);
    const sliceImg = sliceCanvas.toDataURL('image/jpeg', 0.95);
    const sliceHmm = sliceHpx / pxPerMm;
    if (pageIndex > 0) pdf.addPage();
    pdf.addImage(sliceImg, 'JPEG', MARGIN_LEFT_MM, MARGIN_TOP_MM, CONTENT_W_MM, sliceHmm);
    cursorPx += sliceHpx;
    pageIndex += 1;
  }
  pdf.save(filename);
}
export const MaternityFiles: React.FC = () => {
  const {
    maternityFiles,
    deleteMaternityFile,
    currentUser
  } = useData();
  const isAdmin = currentUser?.role === 'Administrator';
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFile, setSelectedFile] = useState<MaternityFile | null>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const printableRef = useRef<HTMLDivElement>(null);
  const handleDeleteFile = (file: MaternityFile) => {
    if (window.confirm(`WARNING: This will permanently delete the maternity file for ${file.patient.firstName} ${file.patient.surname} (${file.bmcFileNumber}) and ALL of its records. This cannot be undone. Are you sure?`)) {
      deleteMaternityFile(file.id);
      setSelectedFile(null);
    }
  };
  const filteredFiles = maternityFiles.filter(m => {
    const q = searchQuery.toLowerCase();
    return m.patient.firstName.toLowerCase().includes(q) || m.patient.surname.toLowerCase().includes(q) || m.bmcFileNumber.toLowerCase().includes(q);
  }).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  const handlePrint = () => {
    document.body.classList.add('printing-maternity-modal');
    window.print();
    setTimeout(() => {
      document.body.classList.remove('printing-maternity-modal');
    }, 500);
  };
  const handleDownloadPdf = async () => {
    if (!printableRef.current || !selectedFile) return;
    setIsGeneratingPdf(true);
    try {
      const filename = `BMED_Maternity_${selectedFile.bmcFileNumber}_${selectedFile.patient.firstName}_${selectedFile.patient.surname}.pdf`;
      await captureToA4Pdf(printableRef.current, filename);
    } catch (err) {
      console.error('PDF generation failed', err);
      alert('Failed to generate PDF.');
    } finally {
      setIsGeneratingPdf(false);
    }
  };
  return <div className="space-y-6" data-id="element-3136">
      <div className="flex justify-between items-center print:hidden" data-id="element-3137">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-3138">
          Maternity Patient Files
        </h1>
      </div>

      {!selectedFile ? <Card noPadding data-id="element-3139">
          <div className="p-4 border-b border-gray-200 bg-gray-50" data-id="element-3140">
            <div className="relative max-w-md" data-id="element-3141">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3142">
                <Search className="h-5 w-5 text-gray-400" data-id="element-3143" />
              </div>
              <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-pink-500 focus:border-pink-500 sm:text-sm" placeholder="Search by name or BMC file number..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-3144" />
            </div>
          </div>
          <ul className="divide-y divide-gray-200" data-id="element-3145">
            {filteredFiles.length > 0 ? filteredFiles.map(file => <li key={file.id} className="p-4 hover:bg-gray-50 flex justify-between items-center" data-id="element-3146">
                  <div className="flex items-center" data-id="element-3147">
                    <div className="bg-pink-100 p-2 rounded-full mr-4" data-id="element-3148">
                      <Baby className="h-6 w-6 text-pink-600" data-id="element-3149" />
                    </div>
                    <div data-id="element-3150">
                      <p className="text-sm font-medium text-blue-600" data-id="element-3151">
                        {file.bmcFileNumber}
                      </p>
                      <p className="text-lg font-bold text-gray-900" data-id="element-3152">
                        {file.patient.firstName} {file.patient.surname}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-3153">
                        Registered:{' '}
                        {new Date(file.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4" data-id="element-3154">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${file.stage === 'COMPLETED' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-3155">
                      {file.stage === 'COMPLETED' ? '✓ Completed' : 'Active (Nursing)'}
                    </span>
                    <Button onClick={() => setSelectedFile(file)} data-id="element-3156">
                      <FileText className="w-4 h-4 mr-2" data-id="element-3157" /> View File
                    </Button>
                    {isAdmin && <Button variant="danger" onClick={() => handleDeleteFile(file)} data-id="element-3158">
                        <Trash2 className="w-4 h-4 mr-2" data-id="element-3159" /> Delete
                      </Button>}
                  </div>
                </li>) : <li className="p-12 text-center text-gray-500" data-id="element-3160">
                <Baby className="mx-auto h-12 w-12 text-gray-300 mb-4" data-id="element-3161" />
                <p className="text-lg font-medium text-gray-900" data-id="element-3162">
                  No maternity files found
                </p>
              </li>}
          </ul>
        </Card> : <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 print:p-0 print:bg-white" data-id="element-3163">
          <div className="bg-white rounded-lg shadow-2xl max-w-5xl w-full max-h-[95vh] overflow-y-auto print:max-h-none print:max-w-none print:shadow-none print:rounded-none print:overflow-visible maternity-modal-print" data-id="element-3164">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-3 flex justify-between items-center z-10 print:hidden" data-id="element-3165">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-3166">
                Maternity File — {selectedFile.bmcFileNumber}
              </h3>
              <div className="flex items-center space-x-2" data-id="element-3167">
                <Button variant="outline" size="sm" onClick={handlePrint} data-id="element-3168">
                  <Printer className="w-4 h-4 mr-1" data-id="element-3169" /> Print
                </Button>
                <Button variant="primary" size="sm" onClick={handleDownloadPdf} disabled={isGeneratingPdf} data-id="element-3170">
                  <Download className="w-4 h-4 mr-1" data-id="element-3171" />{' '}
                  {isGeneratingPdf ? 'Generating...' : 'Download PDF'}
                </Button>
                {isAdmin && <Button variant="danger" size="sm" onClick={() => handleDeleteFile(selectedFile)} data-id="element-3172">
                    <Trash2 className="w-4 h-4 mr-1" data-id="element-3173" /> Delete
                  </Button>}
                <button onClick={() => setSelectedFile(null)} className="p-1.5 hover:bg-gray-100 rounded" data-id="element-3174">
                  <X className="w-5 h-5 text-gray-500" data-id="element-3175" />
                </button>
              </div>
            </div>

            <div ref={printableRef} className="p-8 bg-white text-sm font-sans text-black" data-id="element-3176">
              {/* Header */}
              <div className="flex justify-between items-start mb-8 border-b-2 border-black pb-4" data-id="element-3177">
                <div className="flex items-center" data-id="element-3178">
                  <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED" className="h-16 w-16 object-contain mr-3" data-id="element-3179" />
                  <div data-id="element-3180">
                    <h1 className="text-xl font-bold uppercase" data-id="element-3181">
                      BMED MEDICAL CENTRE
                    </h1>
                    <p className="text-xs italic" data-id="element-3182">Caring Healing Leading</p>
                  </div>
                </div>
                <div className="text-right" data-id="element-3183">
                  <h2 className="text-lg font-bold uppercase tracking-widest border-b border-black pb-1 mb-2" data-id="element-3184">
                    Maternity Record
                  </h2>
                  <p data-id="element-3185">
                    <span className="font-semibold" data-id="element-3186">File No:</span>{' '}
                    {selectedFile.bmcFileNumber}
                  </p>
                  <p data-id="element-3187">
                    <span className="font-semibold" data-id="element-3188">Registered:</span>{' '}
                    {new Date(selectedFile.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </div>

              {/* PATIENT'S DETAILS */}
              <div className="mb-6" data-id="element-3189">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-3190">
                  PATIENT'S DETAILS
                </h3>
                <div className="grid grid-cols-2 gap-y-2 gap-x-8" data-id="element-3191">
                  <div data-id="element-3192">
                    <span className="font-semibold" data-id="element-3193">First Name:</span>{' '}
                    {selectedFile.patient.firstName}
                  </div>
                  <div data-id="element-3194">
                    <span className="font-semibold" data-id="element-3195">Surname:</span>{' '}
                    {selectedFile.patient.surname}
                  </div>
                  <div data-id="element-3196">
                    <span className="font-semibold" data-id="element-3197">Other Names:</span>{' '}
                    {selectedFile.patient.otherNames}
                  </div>
                  <div data-id="element-3198">
                    <span className="font-semibold" data-id="element-3199">Age:</span>{' '}
                    {selectedFile.patient.age}
                  </div>
                  <div data-id="element-3200">
                    <span className="font-semibold" data-id="element-3201">Religion:</span>{' '}
                    {selectedFile.patient.religion}
                  </div>
                  <div data-id="element-3202">
                    <span className="font-semibold" data-id="element-3203">Church Attended:</span>{' '}
                    {selectedFile.patient.churchAttended}
                  </div>
                  <div className="col-span-2" data-id="element-3204">
                    <span className="font-semibold" data-id="element-3205">Residential Address:</span>{' '}
                    {selectedFile.patient.residentialAddress}
                  </div>
                  <div className="col-span-2" data-id="element-3206">
                    <span className="font-semibold" data-id="element-3207">Location/Landmark:</span>{' '}
                    {selectedFile.patient.locationLandmark}
                  </div>
                </div>
              </div>

              {/* SPOUSE DETAILS */}
              <div className="mb-6" data-id="element-3208">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-3209">
                  SPOUSE DETAILS (OPTIONAL)
                </h3>
                <div className="grid grid-cols-2 gap-y-2 gap-x-8" data-id="element-3210">
                  <div data-id="element-3211">
                    <span className="font-semibold" data-id="element-3212">First Name:</span>{' '}
                    {selectedFile.spouse.firstName}
                  </div>
                  <div data-id="element-3213">
                    <span className="font-semibold" data-id="element-3214">Surname:</span>{' '}
                    {selectedFile.spouse.surname}
                  </div>
                  <div data-id="element-3215">
                    <span className="font-semibold" data-id="element-3216">Other Names:</span>{' '}
                    {selectedFile.spouse.otherNames}
                  </div>
                  <div data-id="element-3217">
                    <span className="font-semibold" data-id="element-3218">Occupation:</span>{' '}
                    {selectedFile.spouse.occupation}
                  </div>
                  <div className="col-span-2" data-id="element-3219">
                    <span className="font-semibold" data-id="element-3220">Contact:</span>{' '}
                    {selectedFile.spouse.contact}
                  </div>
                </div>
              </div>

              {/* ADMISSION */}
              <div className="mb-6" data-id="element-3221">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-3222">
                  ADMISSION
                </h3>
                <div className="flex space-x-16" data-id="element-3223">
                  <div data-id="element-3224">
                    <span className="font-semibold" data-id="element-3225">Date:</span>{' '}
                    {selectedFile.admission.date}
                  </div>
                  <div data-id="element-3226">
                    <span className="font-semibold" data-id="element-3227">Time of Admission:</span>{' '}
                    {selectedFile.admission.time}
                  </div>
                </div>
              </div>

              {/* GENERAL EXAMINATION */}
              <div className="mb-6" data-id="element-3228">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-3229">
                  GENERAL EXAMINATION
                </h3>
                <div className="space-y-2" data-id="element-3230">
                  <div data-id="element-3231">
                    <span className="font-semibold" data-id="element-3232">
                      Cardiovascular System:
                    </span>{' '}
                    {selectedFile.generalExam.cardiovascularSystem}
                  </div>
                  <div className="flex space-x-8" data-id="element-3233">
                    <div data-id="element-3234">
                      <span className="font-semibold" data-id="element-3235">Temp:</span>{' '}
                      {selectedFile.generalExam.temp}
                    </div>
                    <div data-id="element-3236">
                      <span className="font-semibold" data-id="element-3237">Pulse:</span>{' '}
                      {selectedFile.generalExam.pulse}
                    </div>
                    <div data-id="element-3238">
                      <span className="font-semibold" data-id="element-3239">Respiration:</span>{' '}
                      {selectedFile.generalExam.respiration}
                    </div>
                    <div data-id="element-3240">
                      <span className="font-semibold" data-id="element-3241">BP:</span>{' '}
                      {selectedFile.generalExam.bp}
                    </div>
                  </div>
                  <div className="flex space-x-16" data-id="element-3242">
                    <div data-id="element-3243">
                      <span className="font-semibold" data-id="element-3244">Oedema:</span>{' '}
                      {selectedFile.generalExam.oedema}
                    </div>
                    <div data-id="element-3245">
                      <span className="font-semibold" data-id="element-3246">Anemia:</span>{' '}
                      {selectedFile.generalExam.anemia}
                    </div>
                  </div>
                  <div data-id="element-3247">
                    <span className="font-semibold" data-id="element-3248">Respiratory System:</span>{' '}
                    {selectedFile.generalExam.respiratorySystem}
                  </div>
                  <div data-id="element-3249">
                    <span className="font-semibold" data-id="element-3250">
                      Central Nervous System:
                    </span>{' '}
                    {selectedFile.generalExam.centralNervousSystem}
                  </div>
                  <div className="flex space-x-8" data-id="element-3251">
                    <div data-id="element-3252">
                      <span className="font-semibold" data-id="element-3253">Breasts:</span>{' '}
                      {selectedFile.generalExam.breasts}
                    </div>
                    <div data-id="element-3254">
                      <span className="font-semibold" data-id="element-3255">Varicosities:</span>{' '}
                      {selectedFile.generalExam.varicosities}
                    </div>
                    <div data-id="element-3256">
                      <span className="font-semibold" data-id="element-3257">Vaginal Discharge:</span>{' '}
                      {selectedFile.generalExam.vaginalDischarge}
                    </div>
                  </div>
                  <div data-id="element-3258">
                    <span className="font-semibold" data-id="element-3259">Other Abnormalities:</span>{' '}
                    {selectedFile.generalExam.otherAbnormalities}
                  </div>
                  <div className="flex space-x-8 mt-2" data-id="element-3260">
                    <div data-id="element-3261">
                      <span className="font-semibold" data-id="element-3262">MS Urine: SG:</span>{' '}
                      {selectedFile.generalExam.msUrine.sg}
                    </div>
                    <div data-id="element-3263">
                      <span className="font-semibold" data-id="element-3264">Albumen:</span>{' '}
                      {selectedFile.generalExam.msUrine.albumen}
                    </div>
                    <div data-id="element-3265">
                      <span className="font-semibold" data-id="element-3266">Acetone:</span>{' '}
                      {selectedFile.generalExam.msUrine.acetone}
                    </div>
                    <div data-id="element-3267">
                      <span className="font-semibold" data-id="element-3268">Sugar:</span>{' '}
                      {selectedFile.generalExam.msUrine.sugar}
                    </div>
                  </div>
                  <div className="flex space-x-8" data-id="element-3269">
                    <div data-id="element-3270">
                      <span className="font-semibold" data-id="element-3271">Hb%:</span>{' '}
                      {selectedFile.generalExam.hb}{' '}
                      <span className="italic text-xs" data-id="element-3272">
                        (NOTE: Refer to UTH if HB% is 6grams or below)
                      </span>
                    </div>
                    <div data-id="element-3273">
                      <span className="font-semibold" data-id="element-3274">ABO:</span>{' '}
                      {selectedFile.generalExam.abo}
                    </div>
                    <div data-id="element-3275">
                      <span className="font-semibold" data-id="element-3276">Rh:</span>{' '}
                      {selectedFile.generalExam.rh}
                    </div>
                  </div>
                </div>
              </div>

              {/* PRESENT PREGNANCY */}
              <div className="mb-6" data-id="element-3277">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-3278">
                  PRESENT PREGNANCY
                </h3>
                <div className="space-y-2" data-id="element-3279">
                  <div className="flex space-x-8" data-id="element-3280">
                    <div data-id="element-3281">
                      <span className="font-semibold" data-id="element-3282">LMP:</span>{' '}
                      {selectedFile.presentPregnancy.lmp}
                    </div>
                    <div data-id="element-3283">
                      <span className="font-semibold" data-id="element-3284">Quickening:</span>{' '}
                      {selectedFile.presentPregnancy.quickening}
                    </div>
                    <div data-id="element-3285">
                      <span className="font-semibold" data-id="element-3286">EDD:</span>{' '}
                      {selectedFile.presentPregnancy.edd}
                    </div>
                    <div data-id="element-3287">
                      <span className="font-semibold" data-id="element-3288">Gestation Age:</span>{' '}
                      {selectedFile.presentPregnancy.gestationAge}
                    </div>
                  </div>
                  <div className="flex space-x-8" data-id="element-3289">
                    <div data-id="element-3290">
                      <span className="font-semibold" data-id="element-3291">
                        Summary of ANC Findings 1st BP:
                      </span>{' '}
                      {selectedFile.presentPregnancy.ancSummaryFirstBp}
                    </div>
                    <div data-id="element-3292">
                      <span className="font-semibold" data-id="element-3293">at:</span>{' '}
                      {selectedFile.presentPregnancy.ancSummaryAtWeeks} Weeks
                    </div>
                  </div>
                  <div className="flex space-x-8" data-id="element-3294">
                    <div data-id="element-3295">
                      <span className="font-semibold" data-id="element-3296">
                        Most recent Syphilis Test Date:
                      </span>{' '}
                      {selectedFile.presentPregnancy.syphilisTest.date}
                    </div>
                    <div data-id="element-3297">
                      <span className="font-semibold" data-id="element-3298">Result:</span>{' '}
                      {selectedFile.presentPregnancy.syphilisTest.result}
                    </div>
                    <div data-id="element-3299">
                      <span className="font-semibold" data-id="element-3300">Treatment:</span>{' '}
                      {selectedFile.presentPregnancy.syphilisTest.treatment}
                    </div>
                  </div>
                  <div className="flex space-x-8" data-id="element-3301">
                    <div data-id="element-3302">
                      <span className="font-semibold" data-id="element-3303">
                        Most recent HP Test Date:
                      </span>{' '}
                      {selectedFile.presentPregnancy.hpTest.date}
                    </div>
                    <div data-id="element-3304">
                      <span className="font-semibold" data-id="element-3305">Result:</span>{' '}
                      {selectedFile.presentPregnancy.hpTest.result}
                    </div>
                  </div>
                  <div data-id="element-3306">
                    <span className="font-semibold" data-id="element-3307">Pelvic Assessment:</span>{' '}
                    {selectedFile.presentPregnancy.pelvicAssessment}
                  </div>
                  <div data-id="element-3308">
                    <span className="font-semibold" data-id="element-3309">Abnormalities:</span>{' '}
                    {selectedFile.presentPregnancy.abnormalities}
                  </div>
                </div>
              </div>

              {/* OBSERVATIONS FOR LATENT PHASE LABOUR */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-3310">
                <h3 className="font-bold uppercase underline mb-1" data-id="element-3311">
                  OBSERVATIONS FOR LATENT PHASE LABOUR
                </h3>
                <p className="text-xs italic mb-2" data-id="element-3312">
                  NOTE: Refer to UTH if latest phase exceeds 8 hours
                </p>
                <table className="w-full text-xs border-collapse border border-black text-center" data-id="element-3313">
                  <thead data-id="element-3314">
                    <tr className="bg-gray-100" data-id="element-3315">
                      <th className="border border-black p-1" data-id="element-3316">Date</th>
                      <th className="border border-black p-1" data-id="element-3317">Time</th>
                      <th className="border border-black p-1" data-id="element-3318">Pulse</th>
                      <th className="border border-black p-1" data-id="element-3319">BP</th>
                      <th className="border border-black p-1" data-id="element-3320">FH</th>
                      <th className="border border-black p-1" data-id="element-3321">Temp</th>
                      <th className="border border-black p-1" data-id="element-3322">Urinalysis</th>
                      <th className="border border-black p-1" data-id="element-3323">Contractions</th>
                      <th className="border border-black p-1" data-id="element-3324">
                        Cervical Dilation
                      </th>
                      <th className="border border-black p-1" data-id="element-3325">
                        Membranes Liquor
                      </th>
                      <th className="border border-black p-1" data-id="element-3326">Remarks</th>
                      <th className="border border-black p-1" data-id="element-3327">Sign</th>
                    </tr>
                  </thead>
                  <tbody data-id="element-3328">
                    {selectedFile.labourObservations.length > 0 ? selectedFile.labourObservations.map(obs => <tr key={obs.id} data-id="element-3329">
                          <td className="border border-black p-1" data-id="element-3330">
                            {obs.date}
                          </td>
                          <td className="border border-black p-1" data-id="element-3331">
                            {obs.time}
                          </td>
                          <td className="border border-black p-1" data-id="element-3332">
                            {obs.pulse}
                          </td>
                          <td className="border border-black p-1" data-id="element-3333">{obs.bp}</td>
                          <td className="border border-black p-1" data-id="element-3334">{obs.fh}</td>
                          <td className="border border-black p-1" data-id="element-3335">
                            {obs.temp}
                          </td>
                          <td className="border border-black p-1" data-id="element-3336">
                            {obs.urinalysis}
                          </td>
                          <td className="border border-black p-1" data-id="element-3337">
                            {obs.contractions}
                          </td>
                          <td className="border border-black p-1" data-id="element-3338">
                            {obs.cervicalDilation}
                          </td>
                          <td className="border border-black p-1" data-id="element-3339">
                            {obs.membranesLiquor}
                          </td>
                          <td className="border border-black p-1 text-left" data-id="element-3340">
                            {obs.remarks}
                          </td>
                          <td className="border border-black p-1" data-id="element-3341">
                            {obs.sign}
                          </td>
                        </tr>) : <tr data-id="element-3342">
                        <td colSpan={12} className="border border-black p-4 text-gray-400 italic" data-id="element-3343">
                          No observations recorded
                        </td>
                      </tr>}
                  </tbody>
                </table>
              </div>

              {/* DESCRIPTION OF LABOUR AND DELIVERY */}
              <div className="mb-6" data-id="element-3344">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-3345">
                  DESCRIPTION OF LABOUR AND DELIVERY
                </h3>
                <div className="grid grid-cols-2 gap-x-8 gap-y-2" data-id="element-3346">
                  <div data-id="element-3347">
                    <span className="font-semibold" data-id="element-3348">Laceration:</span>{' '}
                    {selectedFile.delivery.laceration} |{' '}
                    <span className="font-semibold" data-id="element-3349">Anterior:</span>{' '}
                    {selectedFile.delivery.anterior} |{' '}
                    <span className="font-semibold" data-id="element-3350">Posterior:</span>{' '}
                    {selectedFile.delivery.posterior} |{' '}
                    <span className="font-semibold" data-id="element-3351">None:</span>{' '}
                    {selectedFile.delivery.none}
                  </div>
                  <div data-id="element-3352">
                    <span className="font-semibold" data-id="element-3353">Repaired:</span>{' '}
                    {selectedFile.delivery.repaired}
                  </div>
                  <div data-id="element-3354">
                    <span className="font-semibold" data-id="element-3355">Membranes:</span>{' '}
                    {selectedFile.delivery.membranes}
                  </div>
                  <div data-id="element-3356">
                    <span className="font-semibold" data-id="element-3357">Date:</span>{' '}
                    {selectedFile.delivery.membranesDate}{' '}
                    <span className="font-semibold ml-4" data-id="element-3358">Time:</span>{' '}
                    {selectedFile.delivery.membranesTime}
                  </div>
                  <div data-id="element-3359">
                    <span className="font-semibold" data-id="element-3360">Episiotomy:</span>{' '}
                    {selectedFile.delivery.episiotomy}
                  </div>
                  <div data-id="element-3361">
                    <span className="font-semibold" data-id="element-3362">Repaired:</span>{' '}
                    {selectedFile.delivery.episiotomyRepaired}
                  </div>
                  <div data-id="element-3363">
                    <span className="font-semibold" data-id="element-3364">Maternal ARV:</span>{' '}
                    {selectedFile.delivery.maternalARV}
                  </div>
                  <div data-id="element-3365">
                    <span className="font-semibold" data-id="element-3366">
                      Which ones (NPV, AZT, OTHER):
                    </span>{' '}
                    {selectedFile.delivery.arvTypes}
                  </div>
                  <div className="col-span-2 flex space-x-8" data-id="element-3367">
                    <div data-id="element-3368">
                      <span className="font-semibold" data-id="element-3369">Uterine massage:</span>{' '}
                      {selectedFile.delivery.uterineMassage}
                    </div>
                    <div data-id="element-3370">
                      <span className="font-semibold" data-id="element-3371">
                        Mother Already on HAART:
                      </span>{' '}
                      {selectedFile.delivery.motherOnHAART}
                    </div>
                    <div data-id="element-3372">
                      <span className="font-semibold" data-id="element-3373">Cytotec:</span>{' '}
                      {selectedFile.delivery.cytotec}
                    </div>
                    <div data-id="element-3374">
                      <span className="font-semibold" data-id="element-3375">Syntometrine:</span>{' '}
                      {selectedFile.delivery.syntometrine}
                    </div>
                  </div>
                  <div data-id="element-3376">
                    <span className="font-semibold" data-id="element-3377">1st stage began Date:</span>{' '}
                    {selectedFile.delivery.firstStageBeganDate}{' '}
                    <span className="font-semibold ml-4" data-id="element-3378">Time:</span>{' '}
                    {selectedFile.delivery.firstStageBeganTime}
                  </div>
                  <div data-id="element-3379">
                    <span className="font-semibold" data-id="element-3380">Placenta Delivery:</span>{' '}
                    {selectedFile.delivery.placentaDelivery}
                  </div>
                  <div data-id="element-3381">
                    <span className="font-semibold" data-id="element-3382">
                      Complete dilation Date:
                    </span>{' '}
                    {selectedFile.delivery.completeDilationDate}{' '}
                    <span className="font-semibold ml-4" data-id="element-3383">Time:</span>{' '}
                    {selectedFile.delivery.completeDilationTime}
                  </div>
                  <div data-id="element-3384">
                    <span className="font-semibold" data-id="element-3385">
                      Placenta Delivered Date:
                    </span>{' '}
                    {selectedFile.delivery.placentaDeliveredDate}{' '}
                    <span className="font-semibold ml-4" data-id="element-3386">Time:</span>{' '}
                    {selectedFile.delivery.placentaDeliveredTime}
                  </div>
                  <div className="col-span-2" data-id="element-3387">
                    <span className="font-semibold" data-id="element-3388">
                      Abnormality at Dilation:
                    </span>{' '}
                    {selectedFile.delivery.abnormalityAtDilation} |{' '}
                    <span className="font-semibold" data-id="element-3389">Describe:</span>{' '}
                    {selectedFile.delivery.abnormalityAtDilationDesc}
                  </div>
                  <div data-id="element-3390">
                    <span className="font-semibold" data-id="element-3391">
                      Postpartum hemorrhage:
                    </span>{' '}
                    {selectedFile.delivery.postpartumHemorrhage}
                  </div>
                  <div data-id="element-3392">
                    <span className="font-semibold" data-id="element-3393">Delivery Method:</span>{' '}
                    {selectedFile.delivery.deliveryMethod}
                  </div>
                  <div data-id="element-3394">
                    <span className="font-semibold" data-id="element-3395">EBL:</span>{' '}
                    {selectedFile.delivery.ebl} cc
                  </div>
                  <div className="col-span-2" data-id="element-3396">
                    <span className="font-semibold" data-id="element-3397">
                      Abnormality at Delivery:
                    </span>{' '}
                    {selectedFile.delivery.abnormalityAtDelivery} |{' '}
                    <span className="font-semibold" data-id="element-3398">Describe:</span>{' '}
                    {selectedFile.delivery.abnormalityAtDeliveryDesc}
                  </div>
                  <div className="col-span-2" data-id="element-3399">
                    <span className="font-semibold" data-id="element-3400">Complications:</span>{' '}
                    {selectedFile.delivery.complications}
                  </div>
                </div>

                <div className="mt-4" data-id="element-3401">
                  <h4 className="font-bold text-sm mb-1" data-id="element-3402">DELIVERY SUMMARY:</h4>
                  <table className="w-full text-xs border-collapse border border-black text-center" data-id="element-3403">
                    <thead data-id="element-3404">
                      <tr className="bg-gray-100" data-id="element-3405">
                        <th className="border border-black p-1" data-id="element-3406">DATE</th>
                        <th className="border border-black p-1" data-id="element-3407">
                          TIME OF BIRTH
                        </th>
                        <th className="border border-black p-1" data-id="element-3408">SEX</th>
                        <th className="border border-black p-1" data-id="element-3409">WEIGHT</th>
                        <th className="border border-black p-1" data-id="element-3410">
                          DELIVERED BY
                        </th>
                      </tr>
                    </thead>
                    <tbody data-id="element-3411">
                      {selectedFile.delivery.deliverySummary.length > 0 ? selectedFile.delivery.deliverySummary.map(sum => <tr key={sum.id} data-id="element-3412">
                            <td className="border border-black p-1" data-id="element-3413">
                              {sum.date}
                            </td>
                            <td className="border border-black p-1" data-id="element-3414">
                              {sum.timeOfBirth}
                            </td>
                            <td className="border border-black p-1" data-id="element-3415">
                              {sum.sex}
                            </td>
                            <td className="border border-black p-1" data-id="element-3416">
                              {sum.weight}
                            </td>
                            <td className="border border-black p-1" data-id="element-3417">
                              {sum.deliveredBy}
                            </td>
                          </tr>) : <tr data-id="element-3418">
                          <td colSpan={5} className="border border-black p-4 text-gray-400 italic" data-id="element-3419">
                            No delivery summary recorded
                          </td>
                        </tr>}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* NEW BORN EVALUATION */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-3420">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-3421">
                  NEW BORN EVALUATION
                </h3>
                <div className="grid grid-cols-2 gap-x-8 gap-y-2" data-id="element-3422">
                  <div data-id="element-3423">
                    <span className="font-semibold" data-id="element-3424">EGA:</span>{' '}
                    {selectedFile.newbornEvaluation.ega}
                  </div>
                  <div data-id="element-3425">
                    <span className="font-semibold" data-id="element-3426">ALIVE:</span>{' '}
                    {selectedFile.newbornEvaluation.alive}
                  </div>
                  <div data-id="element-3427">
                    <span className="font-semibold" data-id="element-3428">Neonatal Death:</span>{' '}
                    {selectedFile.newbornEvaluation.neonatalDeath}
                  </div>
                  <div data-id="element-3429">
                    <span className="font-semibold" data-id="element-3430">
                      If still born or death? Cause:
                    </span>{' '}
                    {selectedFile.newbornEvaluation.causeOfDeath}
                  </div>
                  <div className="col-span-2 flex space-x-8" data-id="element-3431">
                    <div data-id="element-3432">
                      <span className="font-semibold" data-id="element-3433">Apgar 1 Minutes:</span>{' '}
                      {selectedFile.newbornEvaluation.apgar1}
                    </div>
                    <div data-id="element-3434">
                      <span className="font-semibold" data-id="element-3435">Apgar 5 Minutes:</span>{' '}
                      {selectedFile.newbornEvaluation.apgar5}
                    </div>
                    <div data-id="element-3436">
                      <span className="font-semibold" data-id="element-3437">Apgar 10 Minutes:</span>{' '}
                      {selectedFile.newbornEvaluation.apgar10}
                    </div>
                  </div>
                  <div data-id="element-3438">
                    <span className="font-semibold" data-id="element-3439">General Exam:</span>{' '}
                    {selectedFile.newbornEvaluation.generalExam}
                  </div>
                  <div data-id="element-3440">
                    <span className="font-semibold" data-id="element-3441">
                      Congenital Malformation:
                    </span>{' '}
                    {selectedFile.newbornEvaluation.congenitalMalformation}
                  </div>
                  <div className="col-span-2" data-id="element-3442">
                    <span className="font-semibold" data-id="element-3443">If Yes Describe:</span>{' '}
                    {selectedFile.newbornEvaluation.congenitalMalformationDesc}
                  </div>
                  <div data-id="element-3444">
                    <span className="font-semibold" data-id="element-3445">Infant ARVS?:</span>{' '}
                    {selectedFile.newbornEvaluation.infantARVs}
                  </div>
                  <div data-id="element-3446">
                    <span className="font-semibold" data-id="element-3447">Inf Feeding:</span>{' '}
                    {selectedFile.newbornEvaluation.infantFeeding}
                  </div>
                  <div className="col-span-2" data-id="element-3448">
                    <span className="font-semibold" data-id="element-3449">
                      RESUSCITATION METHODS:
                    </span>{' '}
                    {selectedFile.newbornEvaluation.resuscitationMethods}
                  </div>
                </div>

                <div className="mt-4" data-id="element-3450">
                  <h4 className="font-bold text-sm mb-1 underline" data-id="element-3451">
                    NEWBORN EXAMINATION
                  </h4>
                  <div className="grid grid-cols-2 gap-x-8 gap-y-2" data-id="element-3452">
                    <div data-id="element-3453">
                      <span className="font-semibold" data-id="element-3454">CROWN HEEL LENGTH:</span>{' '}
                      {selectedFile.newbornEvaluation.crownHeelLength}
                    </div>
                    <div data-id="element-3455">
                      <span className="font-semibold" data-id="element-3456">HEAD CIRCUMFERENCE:</span>{' '}
                      {selectedFile.newbornEvaluation.headCircumference}
                    </div>
                    <div data-id="element-3457">
                      <span className="font-semibold" data-id="element-3458">Skin: Jaundice:</span>{' '}
                      {selectedFile.newbornEvaluation.skinJaundice}
                    </div>
                    <div data-id="element-3459">
                      <span className="font-semibold" data-id="element-3460">Other:</span>{' '}
                      {selectedFile.newbornEvaluation.skinOther}
                    </div>
                    <div data-id="element-3461">
                      <span className="font-semibold" data-id="element-3462">Fontanelles:</span>{' '}
                      {selectedFile.newbornEvaluation.fontanelles}
                    </div>
                    <div data-id="element-3463">
                      <span className="font-semibold" data-id="element-3464">Eyes:</span>{' '}
                      {selectedFile.newbornEvaluation.eyes} |{' '}
                      <span className="font-semibold" data-id="element-3465">Treatment:</span>{' '}
                      {selectedFile.newbornEvaluation.eyesTreatment}
                    </div>
                    <div data-id="element-3466">
                      <span className="font-semibold" data-id="element-3467">Mouth:</span>{' '}
                      {selectedFile.newbornEvaluation.mouth}
                    </div>
                    <div data-id="element-3468">
                      <span className="font-semibold" data-id="element-3469">Neck:</span>{' '}
                      {selectedFile.newbornEvaluation.neck}
                    </div>
                    <div data-id="element-3470">
                      <span className="font-semibold" data-id="element-3471">Chest:</span>{' '}
                      {selectedFile.newbornEvaluation.chest}
                    </div>
                    <div data-id="element-3472">
                      <span className="font-semibold" data-id="element-3473">Abdomen:</span>{' '}
                      {selectedFile.newbornEvaluation.abdomen}
                    </div>
                    <div data-id="element-3474">
                      <span className="font-semibold" data-id="element-3475">Umbilicus:</span>{' '}
                      {selectedFile.newbornEvaluation.umbilicus}
                    </div>
                    <div data-id="element-3476">
                      <span className="font-semibold" data-id="element-3477">Genitalia:</span>{' '}
                      {selectedFile.newbornEvaluation.genitalia} |{' '}
                      <span className="font-semibold" data-id="element-3478">Urine:</span>{' '}
                      {selectedFile.newbornEvaluation.urine}
                    </div>
                    <div data-id="element-3479">
                      <span className="font-semibold" data-id="element-3480">Anus:</span>{' '}
                      {selectedFile.newbornEvaluation.anus}
                    </div>
                    <div data-id="element-3481">
                      <span className="font-semibold" data-id="element-3482">Meconium:</span>{' '}
                      {selectedFile.newbornEvaluation.meconium}
                    </div>
                    <div data-id="element-3483">
                      <span className="font-semibold" data-id="element-3484">Upper Limbs:</span>{' '}
                      {selectedFile.newbornEvaluation.upperLimbs}
                    </div>
                    <div data-id="element-3485">
                      <span className="font-semibold" data-id="element-3486">ERB Paralysis:</span>{' '}
                      {selectedFile.newbornEvaluation.erbParalysis}
                    </div>
                    <div data-id="element-3487">
                      <span className="font-semibold" data-id="element-3488">Lower Limbs:</span>{' '}
                      {selectedFile.newbornEvaluation.lowerLimbs}
                    </div>
                    <div data-id="element-3489">
                      <span className="font-semibold" data-id="element-3490">Ortolani Sign:</span>{' '}
                      {selectedFile.newbornEvaluation.ortolaniSign}
                    </div>
                    <div className="col-span-2" data-id="element-3491">
                      <span className="font-semibold" data-id="element-3492">Back:</span>{' '}
                      {selectedFile.newbornEvaluation.back}
                    </div>
                    <div className="col-span-2 flex space-x-8" data-id="element-3493">
                      <span className="font-semibold" data-id="element-3494">Reflexes:</span>
                      <div data-id="element-3495">
                        <span className="font-semibold" data-id="element-3496">Grasp:</span>{' '}
                        {selectedFile.newbornEvaluation.reflexesGrasp}
                      </div>
                      <div data-id="element-3497">
                        <span className="font-semibold" data-id="element-3498">Moro (Startle):</span>{' '}
                        {selectedFile.newbornEvaluation.reflexesMoro}
                      </div>
                      <div data-id="element-3499">
                        <span className="font-semibold" data-id="element-3500">Sucking:</span>{' '}
                        {selectedFile.newbornEvaluation.reflexesSucking}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-4" data-id="element-3501">
                  <span className="font-semibold" data-id="element-3502">NEWBORN PROGRESS NOTES:</span>
                  <p className="mt-1 whitespace-pre-wrap" data-id="element-3503">
                    {selectedFile.newbornEvaluation.progressNotes}
                  </p>
                </div>
              </div>

              {/* POST DELIVERY OBSERVATION CHART */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-3504">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-3505">
                  POST DELIVERY OBSERVATION CHART
                </h3>
                <table className="w-full text-[10px] border-collapse border border-black text-center" data-id="element-3506">
                  <thead data-id="element-3507">
                    <tr className="bg-gray-100" data-id="element-3508">
                      <th className="border border-black p-1" colSpan={2} data-id="element-3509">
                        TIME
                      </th>
                      <th className="border border-black p-1" colSpan={8} data-id="element-3510">
                        MOTHER
                      </th>
                      <th className="border border-black p-1" colSpan={7} data-id="element-3511">
                        BABY
                      </th>
                      <th className="border border-black p-1" rowSpan={2} data-id="element-3512">
                        SIGN
                      </th>
                    </tr>
                    <tr className="bg-gray-100" data-id="element-3513">
                      <th className="border border-black p-1" data-id="element-3514">DATE</th>
                      <th className="border border-black p-1" data-id="element-3515">TIME</th>
                      <th className="border border-black p-1" data-id="element-3516">BP</th>
                      <th className="border border-black p-1" data-id="element-3517">PULSE</th>
                      <th className="border border-black p-1" data-id="element-3518">RESPS</th>
                      <th className="border border-black p-1" data-id="element-3519">TEMP</th>
                      <th className="border border-black p-1" data-id="element-3520">PV BLEEDING</th>
                      <th className="border border-black p-1" data-id="element-3521">UTERUS</th>
                      <th className="border border-black p-1" data-id="element-3522">BLADDER</th>
                      <th className="border border-black p-1" data-id="element-3523">PALLOR</th>
                      <th className="border border-black p-1" data-id="element-3524">TEMP</th>
                      <th className="border border-black p-1" data-id="element-3525">RESPS</th>
                      <th className="border border-black p-1" data-id="element-3526">CORD</th>
                      <th className="border border-black p-1" data-id="element-3527">SKIN COLOUR</th>
                      <th className="border border-black p-1" data-id="element-3528">
                        FEEDING (BF/AF)
                      </th>
                      <th className="border border-black p-1" data-id="element-3529">BOWEL</th>
                      <th className="border border-black p-1" data-id="element-3530">URINE</th>
                    </tr>
                  </thead>
                  <tbody data-id="element-3531">
                    {selectedFile.postDeliveryObservations.length > 0 ? selectedFile.postDeliveryObservations.map(obs => <tr key={obs.id} data-id="element-3532">
                          <td className="border border-black p-1" data-id="element-3533">
                            {obs.date}
                          </td>
                          <td className="border border-black p-1" data-id="element-3534">
                            {obs.time}
                          </td>
                          <td className="border border-black p-1" data-id="element-3535">{obs.bp}</td>
                          <td className="border border-black p-1" data-id="element-3536">
                            {obs.pulse}
                          </td>
                          <td className="border border-black p-1" data-id="element-3537">
                            {obs.resps}
                          </td>
                          <td className="border border-black p-1" data-id="element-3538">
                            {obs.temp}
                          </td>
                          <td className="border border-black p-1" data-id="element-3539">
                            {obs.pvBleeding}
                          </td>
                          <td className="border border-black p-1" data-id="element-3540">
                            {obs.uterus}
                          </td>
                          <td className="border border-black p-1" data-id="element-3541">
                            {obs.bladder}
                          </td>
                          <td className="border border-black p-1" data-id="element-3542">
                            {obs.pallor}
                          </td>
                          <td className="border border-black p-1" data-id="element-3543">
                            {obs.babyTemp}
                          </td>
                          <td className="border border-black p-1" data-id="element-3544">
                            {obs.babyResps}
                          </td>
                          <td className="border border-black p-1" data-id="element-3545">
                            {obs.cord}
                          </td>
                          <td className="border border-black p-1" data-id="element-3546">
                            {obs.skinColour}
                          </td>
                          <td className="border border-black p-1" data-id="element-3547">
                            {obs.feedingBF} / {obs.feedingAF}
                          </td>
                          <td className="border border-black p-1" data-id="element-3548">
                            {obs.eliminationBowel}
                          </td>
                          <td className="border border-black p-1" data-id="element-3549">
                            {obs.eliminationUrine}
                          </td>
                          <td className="border border-black p-1" data-id="element-3550">
                            {obs.sign}
                          </td>
                        </tr>) : <tr data-id="element-3551">
                        <td colSpan={18} className="border border-black p-4 text-gray-400 italic" data-id="element-3552">
                          No observations recorded
                        </td>
                      </tr>}
                  </tbody>
                </table>
              </div>

              {/* DRUG CHART */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-3553">
                <h3 className="font-bold uppercase underline mb-2 text-center" data-id="element-3554">
                  DRUG CHART
                </h3>
                <div className="flex justify-between mb-2 text-xs" data-id="element-3555">
                  <div data-id="element-3556">
                    <span className="font-semibold" data-id="element-3557">NAME:</span>{' '}
                    {selectedFile.patient.firstName}{' '}
                    {selectedFile.patient.surname}
                  </div>
                  <div data-id="element-3558">
                    <span className="font-semibold" data-id="element-3559">WARD:</span>{' '}
                    {selectedFile.drugChart?.ward}
                  </div>
                  <div data-id="element-3560">
                    <span className="font-semibold" data-id="element-3561">No.:</span>{' '}
                    {selectedFile.drugChart?.patientNo}
                  </div>
                  <div data-id="element-3562">
                    <span className="font-semibold" data-id="element-3563">SEX/AGE:</span> Female /{' '}
                    {selectedFile.patient.age}
                  </div>
                </div>

                <table className="w-full text-[9px] border-collapse border border-black text-center" data-id="element-3564">
                  <thead data-id="element-3565">
                    <tr className="bg-gray-100" data-id="element-3566">
                      <th className="border border-black p-1 w-16" rowSpan={3} data-id="element-3567">
                        DATE
                      </th>
                      <th className="border border-black p-1 w-24" rowSpan={3} data-id="element-3568">
                        DRUG
                      </th>
                      <th className="border border-black p-1 w-16" rowSpan={3} data-id="element-3569">
                        DOSAGE
                      </th>
                      <th className="border border-black p-1 w-16" rowSpan={3} data-id="element-3570">
                        ROUTE OF ADMIN
                      </th>
                      <th className="border border-black p-1 w-16" rowSpan={3} data-id="element-3571">
                        ORDERED BY
                      </th>
                      <th className="border border-black p-1" colSpan={21} data-id="element-3572">
                        SUPPLIED BY (DATE)
                      </th>
                      <th className="border border-black p-1 w-16" rowSpan={3} data-id="element-3573">
                        SUPPLIED BY
                      </th>
                      <th className="border border-black p-1 w-12" rowSpan={3} data-id="element-3574">
                        QUANTITY
                      </th>
                      <th className="border border-black p-1 w-16" rowSpan={3} data-id="element-3575">
                        DATE
                      </th>
                    </tr>
                    <tr className="bg-gray-100" data-id="element-3576">
                      {[0, 1, 2].map(colIdx => <th key={colIdx} className="border border-black p-1" colSpan={7} data-id="element-3577">
                          {selectedFile.drugChart?.supplyDates?.[colIdx] || ''}
                        </th>)}
                    </tr>
                    <tr className="bg-gray-100" data-id="element-3578">
                      {[0, 1, 2].map(colIdx => <Fragment key={`times-${colIdx}`} data-id="element-3579">
                          {['6', '10', '12', '14', '18', '22', '24'].map(time => <th key={`${colIdx}-${time}`} className="border border-black p-0.5 font-normal" data-id="element-3580">
                                {time}
                              </th>)}
                        </Fragment>)}
                    </tr>
                  </thead>
                  <tbody data-id="element-3581">
                    {selectedFile.drugChart?.rows && selectedFile.drugChart.rows.length > 0 ? selectedFile.drugChart.rows.map(row => <tr key={row.id} data-id="element-3582">
                          <td className="border border-black p-1" data-id="element-3583">
                            {row.date}
                          </td>
                          <td className="border border-black p-1" data-id="element-3584">
                            {row.drug}
                          </td>
                          <td className="border border-black p-1" data-id="element-3585">
                            {row.dosage}
                          </td>
                          <td className="border border-black p-1" data-id="element-3586">
                            {row.routeOfAdmin}
                          </td>
                          <td className="border border-black p-1" data-id="element-3587">
                            {row.orderedBy}
                          </td>
                          {[0, 1, 2].map(colIdx => <Fragment key={`admin-${colIdx}`} data-id="element-3588">
                              {['6', '10', '12', '14', '18', '22', '24'].map(time => {
                      const key = `${colIdx}-${time}`;
                      return <td key={key} className="border border-black p-0.5" data-id="element-3589">
                                      {row.administration?.[key] || ''}
                                    </td>;
                    })}
                            </Fragment>)}
                          <td className="border border-black p-1" data-id="element-3590">
                            {row.suppliedBy}
                          </td>
                          <td className="border border-black p-1" data-id="element-3591">
                            {row.quantity}
                          </td>
                          <td className="border border-black p-1" data-id="element-3592">
                            {row.suppliedDate}
                          </td>
                        </tr>) : <tr data-id="element-3593">
                        <td colSpan={30} className="border border-black p-4 text-gray-400 italic" data-id="element-3594">
                          No drug chart recorded
                        </td>
                      </tr>}
                  </tbody>
                </table>
                <div className="flex justify-between mt-4 text-xs" data-id="element-3595">
                  <div data-id="element-3596">
                    <span className="font-semibold" data-id="element-3597">DATE:</span>{' '}
                    {selectedFile.drugChart?.signedDate}
                  </div>
                  <div data-id="element-3598">
                    <span className="font-semibold" data-id="element-3599">SIGNATURE:</span>{' '}
                    {selectedFile.drugChart?.signedBy}
                  </div>
                </div>
              </div>

              {/* PUERPERIUM NOTES */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-3600">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-3601">
                  PUERPERIUM NOTES
                </h3>
                <table className="w-full text-sm border-collapse border border-black" data-id="element-3602">
                  <thead data-id="element-3603">
                    <tr className="bg-gray-100" data-id="element-3604">
                      <th className="border border-black p-2 text-left w-32" data-id="element-3605">
                        DATE
                      </th>
                      <th className="border border-black p-2 text-left" data-id="element-3606">
                        NOTES
                      </th>
                      <th className="border border-black p-2 text-left w-48" data-id="element-3607">
                        SIGNATURE
                      </th>
                    </tr>
                  </thead>
                  <tbody data-id="element-3608">
                    {selectedFile.puerperiumNotes.length > 0 ? selectedFile.puerperiumNotes.map(note => <tr key={note.id} data-id="element-3609">
                          <td className="border border-black p-2" data-id="element-3610">
                            {note.date}
                          </td>
                          <td className="border border-black p-2 whitespace-pre-wrap" data-id="element-3611">
                            {note.notes}
                          </td>
                          <td className="border border-black p-2" data-id="element-3612">
                            {note.signature}
                          </td>
                        </tr>) : <tr data-id="element-3613">
                        <td colSpan={3} className="border border-black p-4 text-gray-400 italic text-center" data-id="element-3614">
                          No notes recorded
                        </td>
                      </tr>}
                  </tbody>
                </table>
              </div>

              {/* DISCHARGE SLIP */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-3615">
                <h3 className="font-bold uppercase underline mb-3 text-center" data-id="element-3616">
                  DISCHARGE SLIP FOR LABOUR WARD
                </h3>

                <div className="grid grid-cols-2 gap-x-8 gap-y-2 mb-4 text-sm" data-id="element-3617">
                  <div data-id="element-3618">
                    <span className="font-semibold" data-id="element-3619">SM No:</span>{' '}
                    {selectedFile.dischargeSlip?.smNo}
                  </div>
                  <div data-id="element-3620">
                    <span className="font-semibold" data-id="element-3621">Gravida:</span>{' '}
                    {selectedFile.dischargeSlip?.gravida}
                  </div>
                  <div data-id="element-3622">
                    <span className="font-semibold" data-id="element-3623">Patient's Name:</span>{' '}
                    {selectedFile.dischargeSlip?.patientName || `${selectedFile.patient.firstName} ${selectedFile.patient.surname}`}
                  </div>
                  <div data-id="element-3624">
                    <span className="font-semibold" data-id="element-3625">Party:</span>{' '}
                    {selectedFile.dischargeSlip?.party}
                  </div>
                  <div data-id="element-3626">
                    <span className="font-semibold" data-id="element-3627">Time of Discharge:</span>{' '}
                    {selectedFile.dischargeSlip?.timeOfDischarge}
                  </div>
                  <div data-id="element-3628">
                    <span className="font-semibold" data-id="element-3629">Age:</span>{' '}
                    {selectedFile.dischargeSlip?.age || selectedFile.patient.age}
                  </div>
                </div>

                <h4 className="font-bold uppercase underline mb-2 text-sm" data-id="element-3630">
                  Labour and Delivery Summary
                </h4>
                <div className="grid grid-cols-2 gap-x-8 gap-y-2 mb-4 text-sm border border-black p-3" data-id="element-3631">
                  <div className="font-bold uppercase" data-id="element-3632">Mother</div>
                  <div className="font-bold uppercase" data-id="element-3633">Infant</div>
                  <div data-id="element-3634">
                    <span className="font-semibold" data-id="element-3635">D.O.B:</span>{' '}
                    {selectedFile.dischargeSlip?.motherDob}
                  </div>
                  <div data-id="element-3636">
                    <span className="font-semibold" data-id="element-3637">T.O.B:</span>{' '}
                    {selectedFile.dischargeSlip?.infantTob}
                  </div>
                  <div data-id="element-3638">
                    <span className="font-semibold" data-id="element-3639">M.O.D:</span>{' '}
                    {selectedFile.dischargeSlip?.modeOfDelivery}
                  </div>
                  <div data-id="element-3640">
                    <span className="font-semibold" data-id="element-3641">A/S:</span>{' '}
                    {selectedFile.dischargeSlip?.apgarScore}
                  </div>
                  <div data-id="element-3642">
                    <span className="font-semibold" data-id="element-3643">Perineum:</span>{' '}
                    {selectedFile.dischargeSlip?.perineum}
                  </div>
                  <div data-id="element-3644">
                    <span className="font-semibold" data-id="element-3645">Sex:</span>{' '}
                    {selectedFile.dischargeSlip?.infantSex}
                  </div>
                  <div className="col-span-2" data-id="element-3646">
                    <span className="font-semibold" data-id="element-3647">Comments:</span>{' '}
                    {selectedFile.dischargeSlip?.comments}
                  </div>
                  <div className="col-span-2" data-id="element-3648">
                    <span className="font-semibold" data-id="element-3649">P.C.A:</span>{' '}
                    {selectedFile.dischargeSlip?.pca}
                  </div>
                </div>

                <div className="flex justify-between items-center mb-2" data-id="element-3650">
                  <h4 className="font-bold uppercase underline text-sm" data-id="element-3651">
                    Discharge Summary
                  </h4>
                  <div className="text-sm" data-id="element-3652">
                    <span className="font-semibold" data-id="element-3653">Date of Discharge:</span>{' '}
                    {selectedFile.dischargeSlip?.dateOfDischarge}
                  </div>
                </div>

                <div className="border border-black p-3 mb-4 text-sm" data-id="element-3654">
                  <h5 className="font-bold uppercase mb-2" data-id="element-3655">Mother</h5>
                  <div className="grid grid-cols-2 gap-x-8 gap-y-2" data-id="element-3656">
                    <div data-id="element-3657">
                      <span className="font-semibold" data-id="element-3658">BP:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.bp}
                    </div>
                    <div data-id="element-3659">
                      <span className="font-semibold" data-id="element-3660">Pallor:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.pallor}
                    </div>
                    <div data-id="element-3661">
                      <span className="font-semibold" data-id="element-3662">Breast:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.breast}
                    </div>
                    <div data-id="element-3663">
                      <span className="font-semibold" data-id="element-3664">
                        Breastfeeding (Yes/No):
                      </span>{' '}
                      {selectedFile.dischargeSlip?.mother?.breastfeeding}
                    </div>
                    <div data-id="element-3665">
                      <span className="font-semibold" data-id="element-3666">Uterus:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.uterus}
                    </div>
                    <div data-id="element-3667">
                      <span className="font-semibold" data-id="element-3668">Perineum:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.perineum}
                    </div>
                    <div data-id="element-3669">
                      <span className="font-semibold" data-id="element-3670">Lochia:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.lochia}
                    </div>
                    <div data-id="element-3671">
                      <span className="font-semibold" data-id="element-3672">Bowels:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.bowels}
                    </div>
                    <div data-id="element-3673">
                      <span className="font-semibold" data-id="element-3674">Urine:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.urine}
                    </div>
                    <div data-id="element-3675">
                      <span className="font-semibold" data-id="element-3676">Family Planning:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.familyPlanning}
                    </div>
                    <div className="col-span-2" data-id="element-3677">
                      <span className="font-semibold" data-id="element-3678">Danger Signs:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.dangerSigns}
                    </div>
                    <div className="col-span-2" data-id="element-3679">
                      <span className="font-semibold" data-id="element-3680">Treatment:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.treatment}
                    </div>
                    <div data-id="element-3681">
                      <span className="font-semibold" data-id="element-3682">Vitamin A:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.vitaminA}
                    </div>
                  </div>
                </div>

                <div className="border border-black p-3 mb-4 text-sm" data-id="element-3683">
                  <h5 className="font-bold uppercase mb-2" data-id="element-3684">Infant</h5>
                  <div className="grid grid-cols-2 gap-x-8 gap-y-2" data-id="element-3685">
                    <div data-id="element-3686">
                      <span className="font-semibold" data-id="element-3687">BWT:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.birthWeight}
                    </div>
                    <div data-id="element-3688">
                      <span className="font-semibold" data-id="element-3689">Discharge Weight:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.dischargeWeight}
                    </div>
                    <div data-id="element-3690">
                      <span className="font-semibold" data-id="element-3691">Pallor:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.pallor}
                    </div>
                    <div data-id="element-3692">
                      <span className="font-semibold" data-id="element-3693">Jaundice:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.jaundice}
                    </div>
                    <div className="col-span-2" data-id="element-3694">
                      <span className="font-semibold" data-id="element-3695">
                        Passed Meconium / Urine:
                      </span>{' '}
                      {selectedFile.dischargeSlip?.infant?.passedMeconiumUrine}
                    </div>
                    <div data-id="element-3696">
                      <span className="font-semibold" data-id="element-3697">Feeding:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.feeding}
                    </div>
                    <div data-id="element-3698">
                      <span className="font-semibold" data-id="element-3699">Feeding Option:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.feedingOption}
                    </div>
                    <div data-id="element-3700">
                      <span className="font-semibold" data-id="element-3701">Breast Feeding:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.breastFeeding}
                    </div>
                    <div data-id="element-3702">
                      <span className="font-semibold" data-id="element-3703">BCG:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.bcg}
                    </div>
                    <div data-id="element-3704">
                      <span className="font-semibold" data-id="element-3705">OPV:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.opv}
                    </div>
                    <div data-id="element-3706">
                      <span className="font-semibold" data-id="element-3707">Birth Record:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.birthRecord}
                    </div>
                    <div data-id="element-3708">
                      <span className="font-semibold" data-id="element-3709">G/C:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.gc}
                    </div>
                    <div className="col-span-2" data-id="element-3710">
                      <span className="font-semibold" data-id="element-3711">Treatment:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.treatment}
                    </div>
                  </div>
                </div>

                <div className="space-y-2 text-sm" data-id="element-3712">
                  <div data-id="element-3713">
                    <span className="font-semibold" data-id="element-3714">
                      6 days Postnatal Date:
                    </span>{' '}
                    {selectedFile.dischargeSlip?.sixDaysPostnatalDate}
                  </div>
                  <div data-id="element-3715">
                    <span className="font-semibold" data-id="element-3716">
                      6 Weeks Postnatal Date:
                    </span>{' '}
                    {selectedFile.dischargeSlip?.sixWeeksPostnatalDate}
                  </div>
                  <div data-id="element-3717">
                    <span className="font-semibold" data-id="element-3718">Name in Full:</span>{' '}
                    {selectedFile.dischargeSlip?.nameInFull}
                  </div>
                </div>
              </div>

              {/* POSTNATAL AT ONE WEEK */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-3719">
                <h3 className="font-bold uppercase underline mb-3 text-center" data-id="element-3720">
                  Postnatal at One Week
                </h3>
                <div className="text-sm mb-3" data-id="element-3721">
                  <span className="font-semibold" data-id="element-3722">Date:</span>{' '}
                  {selectedFile.postNatalOneWeek?.date}
                </div>

                <h4 className="font-bold uppercase underline mb-2 text-sm" data-id="element-3723">
                  Mother
                </h4>
                <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm mb-4" data-id="element-3724">
                  <div data-id="element-3725">
                    <span className="font-semibold" data-id="element-3726">General Well Being:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.generalWellBeing}
                  </div>
                  <div data-id="element-3727">
                    <span className="font-semibold" data-id="element-3728">Temp:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.temp}
                  </div>
                  <div data-id="element-3729">
                    <span className="font-semibold" data-id="element-3730">Pulse:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.pulse}
                  </div>
                  <div data-id="element-3731">
                    <span className="font-semibold" data-id="element-3732">BP:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.bp}
                  </div>
                  <div data-id="element-3733">
                    <span className="font-semibold" data-id="element-3734">Urine:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.urine}
                  </div>
                  <div data-id="element-3735">
                    <span className="font-semibold" data-id="element-3736">Head:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.head}
                  </div>
                  <div data-id="element-3737">
                    <span className="font-semibold" data-id="element-3738">Eyes:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.eyes}
                  </div>
                  <div data-id="element-3739">
                    <span className="font-semibold" data-id="element-3740">Ears:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.ears}
                  </div>
                  <div data-id="element-3741">
                    <span className="font-semibold" data-id="element-3742">Mouth:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.mouth}
                  </div>
                  <div data-id="element-3743">
                    <span className="font-semibold" data-id="element-3744">Neck:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.neck}
                  </div>
                  <div data-id="element-3745">
                    <span className="font-semibold" data-id="element-3746">Breasts:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.breasts}
                  </div>
                  <div data-id="element-3747">
                    <span className="font-semibold" data-id="element-3748">Ampills:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.ampills}
                  </div>
                  <div data-id="element-3749">
                    <span className="font-semibold" data-id="element-3750">Abdomen (Uterus):</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.abdomenUterus}
                  </div>
                  <div data-id="element-3751">
                    <span className="font-semibold" data-id="element-3752">Groin:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.groin}
                  </div>
                  <div data-id="element-3753">
                    <span className="font-semibold" data-id="element-3754">Perineum:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.perineum}
                  </div>
                  <div data-id="element-3755">
                    <span className="font-semibold" data-id="element-3756">Legs:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.legs}
                  </div>
                  <div data-id="element-3757">
                    <span className="font-semibold" data-id="element-3758">Anus:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.anus}
                  </div>
                  <div data-id="element-3759">
                    <span className="font-semibold" data-id="element-3760">Lochia:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.lochia}
                  </div>
                </div>

                <h4 className="font-bold uppercase underline mb-2 text-sm" data-id="element-3761">
                  Vaginal Examination
                </h4>
                <div className="space-y-2 text-sm mb-4" data-id="element-3762">
                  <div data-id="element-3763">
                    <span className="font-semibold" data-id="element-3764">Cervix per speculum:</span>{' '}
                    <span className="whitespace-pre-wrap" data-id="element-3765">
                      {selectedFile.postNatalOneWeek?.mother.cervixPerSpeculum}
                    </span>
                  </div>
                  <div data-id="element-3766">
                    <span className="font-semibold" data-id="element-3767">Family Planning:</span>{' '}
                    <span className="whitespace-pre-wrap" data-id="element-3768">
                      {selectedFile.postNatalOneWeek?.mother.familyPlanning}
                    </span>
                  </div>
                  <div data-id="element-3769">
                    <span className="font-semibold" data-id="element-3770">Treatment or advise:</span>{' '}
                    <span className="whitespace-pre-wrap" data-id="element-3771">
                      {selectedFile.postNatalOneWeek?.mother.treatmentOrAdvise}
                    </span>
                  </div>
                </div>

                <h4 className="font-bold uppercase underline mb-2 text-sm" data-id="element-3772">
                  Infant Examination Form
                </h4>
                <div className="grid grid-cols-3 gap-x-8 gap-y-1 text-sm mb-3" data-id="element-3773">
                  <div data-id="element-3774">
                    <span className="font-semibold" data-id="element-3775">Infant:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.name}
                  </div>
                  <div data-id="element-3776">
                    <span className="font-semibold" data-id="element-3777">Weight:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.weight}
                  </div>
                  <div data-id="element-3778">
                    <span className="font-semibold" data-id="element-3779">Date:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.date}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm mb-3" data-id="element-3780">
                  <div data-id="element-3781">
                    <span className="font-semibold" data-id="element-3782">Head Circumference:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.headCircumference}
                  </div>
                  <div data-id="element-3783">
                    <span className="font-semibold" data-id="element-3784">Length:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.length}
                  </div>
                  <div data-id="element-3785">
                    <span className="font-semibold" data-id="element-3786">Temp:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.temp}
                  </div>
                  <div data-id="element-3787">
                    <span className="font-semibold" data-id="element-3788">Head:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.head}
                  </div>
                  <div data-id="element-3789">
                    <span className="font-semibold" data-id="element-3790">Eyes:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.eyes}
                  </div>
                  <div data-id="element-3791">
                    <span className="font-semibold" data-id="element-3792">Ears:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.ears}
                  </div>
                  <div data-id="element-3793">
                    <span className="font-semibold" data-id="element-3794">Mouth:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.mouth}
                  </div>
                  <div data-id="element-3795">
                    <span className="font-semibold" data-id="element-3796">Neck:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.neck}
                  </div>
                  <div data-id="element-3797">
                    <span className="font-semibold" data-id="element-3798">Chest:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.chest}
                  </div>
                  <div data-id="element-3799">
                    <span className="font-semibold" data-id="element-3800">Abdomen:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.abdomen}
                  </div>
                  <div data-id="element-3801">
                    <span className="font-semibold" data-id="element-3802">Cord:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.cord}
                  </div>
                  <div data-id="element-3803">
                    <span className="font-semibold" data-id="element-3804">Genitalia & Rectum:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.genitaliaRectum}
                  </div>
                  <div data-id="element-3805">
                    <span className="font-semibold" data-id="element-3806">Extremities:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.extremities}
                  </div>
                  <div data-id="element-3807">
                    <span className="font-semibold" data-id="element-3808">Back:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.back}
                  </div>
                  <div data-id="element-3809">
                    <span className="font-semibold" data-id="element-3810">Neurologic Exam:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.neurologicExam}
                  </div>
                  <div data-id="element-3811">
                    <span className="font-semibold" data-id="element-3812">Feeding:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.feeding}
                  </div>
                </div>
                <div className="text-sm" data-id="element-3813">
                  <span className="font-semibold" data-id="element-3814">Additional Notes:</span>
                  <p className="whitespace-pre-wrap mt-1" data-id="element-3815">
                    {selectedFile.postNatalOneWeek?.infant.additionalNotes}
                  </p>
                </div>
              </div>

              {/* POSTNATAL AT SIX WEEKS */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-3816">
                <h3 className="font-bold uppercase underline mb-3 text-center" data-id="element-3817">
                  Postnatal at Six Weeks
                </h3>
                <div className="text-sm mb-3" data-id="element-3818">
                  <span className="font-semibold" data-id="element-3819">Date:</span>{' '}
                  {selectedFile.postNatalSixWeeks?.date}
                </div>

                <h4 className="font-bold uppercase underline mb-2 text-sm" data-id="element-3820">
                  Mother
                </h4>
                <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm mb-4" data-id="element-3821">
                  <div data-id="element-3822">
                    <span className="font-semibold" data-id="element-3823">General Well Being:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.generalWellBeing}
                  </div>
                  <div data-id="element-3824">
                    <span className="font-semibold" data-id="element-3825">Temp:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.temp}
                  </div>
                  <div data-id="element-3826">
                    <span className="font-semibold" data-id="element-3827">Pulse:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.pulse}
                  </div>
                  <div data-id="element-3828">
                    <span className="font-semibold" data-id="element-3829">BP:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.bp}
                  </div>
                  <div data-id="element-3830">
                    <span className="font-semibold" data-id="element-3831">Urine:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.urine}
                  </div>
                  <div data-id="element-3832">
                    <span className="font-semibold" data-id="element-3833">Head:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.head}
                  </div>
                  <div data-id="element-3834">
                    <span className="font-semibold" data-id="element-3835">Eyes:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.eyes}
                  </div>
                  <div data-id="element-3836">
                    <span className="font-semibold" data-id="element-3837">Ears:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.ears}
                  </div>
                  <div data-id="element-3838">
                    <span className="font-semibold" data-id="element-3839">Mouth:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.mouth}
                  </div>
                  <div data-id="element-3840">
                    <span className="font-semibold" data-id="element-3841">Neck:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.neck}
                  </div>
                  <div data-id="element-3842">
                    <span className="font-semibold" data-id="element-3843">Breasts:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.breasts}
                  </div>
                  <div data-id="element-3844">
                    <span className="font-semibold" data-id="element-3845">Ampills:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.ampills}
                  </div>
                  <div data-id="element-3846">
                    <span className="font-semibold" data-id="element-3847">Abdomen (Uterus):</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.abdomenUterus}
                  </div>
                  <div data-id="element-3848">
                    <span className="font-semibold" data-id="element-3849">Groin:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.groin}
                  </div>
                  <div data-id="element-3850">
                    <span className="font-semibold" data-id="element-3851">Perineum:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.perineum}
                  </div>
                  <div data-id="element-3852">
                    <span className="font-semibold" data-id="element-3853">Legs:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.legs}
                  </div>
                  <div data-id="element-3854">
                    <span className="font-semibold" data-id="element-3855">Anus:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.anus}
                  </div>
                  <div data-id="element-3856">
                    <span className="font-semibold" data-id="element-3857">Lochia:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.lochia}
                  </div>
                </div>

                <h4 className="font-bold uppercase underline mb-2 text-sm" data-id="element-3858">
                  Vaginal Examination
                </h4>
                <div className="space-y-2 text-sm mb-4" data-id="element-3859">
                  <div data-id="element-3860">
                    <span className="font-semibold" data-id="element-3861">Cervix per speculum:</span>{' '}
                    <span className="whitespace-pre-wrap" data-id="element-3862">
                      {selectedFile.postNatalSixWeeks?.mother.cervixPerSpeculum}
                    </span>
                  </div>
                  <div data-id="element-3863">
                    <span className="font-semibold" data-id="element-3864">Family Planning:</span>{' '}
                    <span className="whitespace-pre-wrap" data-id="element-3865">
                      {selectedFile.postNatalSixWeeks?.mother.familyPlanning}
                    </span>
                  </div>
                  <div data-id="element-3866">
                    <span className="font-semibold" data-id="element-3867">Treatment or advise:</span>{' '}
                    <span className="whitespace-pre-wrap" data-id="element-3868">
                      {selectedFile.postNatalSixWeeks?.mother.treatmentOrAdvise}
                    </span>
                  </div>
                </div>

                <h4 className="font-bold uppercase underline mb-2 text-sm" data-id="element-3869">
                  Infant Examination Form
                </h4>
                <div className="grid grid-cols-3 gap-x-8 gap-y-1 text-sm mb-3" data-id="element-3870">
                  <div data-id="element-3871">
                    <span className="font-semibold" data-id="element-3872">Infant:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.name}
                  </div>
                  <div data-id="element-3873">
                    <span className="font-semibold" data-id="element-3874">Weight:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.weight}
                  </div>
                  <div data-id="element-3875">
                    <span className="font-semibold" data-id="element-3876">Date:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.date}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm mb-3" data-id="element-3877">
                  <div data-id="element-3878">
                    <span className="font-semibold" data-id="element-3879">Head Circumference:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.headCircumference}
                  </div>
                  <div data-id="element-3880">
                    <span className="font-semibold" data-id="element-3881">Length:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.length}
                  </div>
                  <div data-id="element-3882">
                    <span className="font-semibold" data-id="element-3883">Temp:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.temp}
                  </div>
                  <div data-id="element-3884">
                    <span className="font-semibold" data-id="element-3885">Head:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.head}
                  </div>
                  <div data-id="element-3886">
                    <span className="font-semibold" data-id="element-3887">Eyes:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.eyes}
                  </div>
                  <div data-id="element-3888">
                    <span className="font-semibold" data-id="element-3889">Ears:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.ears}
                  </div>
                  <div data-id="element-3890">
                    <span className="font-semibold" data-id="element-3891">Mouth:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.mouth}
                  </div>
                  <div data-id="element-3892">
                    <span className="font-semibold" data-id="element-3893">Neck:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.neck}
                  </div>
                  <div data-id="element-3894">
                    <span className="font-semibold" data-id="element-3895">Chest:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.chest}
                  </div>
                  <div data-id="element-3896">
                    <span className="font-semibold" data-id="element-3897">Abdomen:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.abdomen}
                  </div>
                  <div data-id="element-3898">
                    <span className="font-semibold" data-id="element-3899">Cord:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.cord}
                  </div>
                  <div data-id="element-3900">
                    <span className="font-semibold" data-id="element-3901">Genitalia & Rectum:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.genitaliaRectum}
                  </div>
                  <div data-id="element-3902">
                    <span className="font-semibold" data-id="element-3903">Extremities:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.extremities}
                  </div>
                  <div data-id="element-3904">
                    <span className="font-semibold" data-id="element-3905">Back:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.back}
                  </div>
                  <div data-id="element-3906">
                    <span className="font-semibold" data-id="element-3907">Neurologic Exam:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.neurologicExam}
                  </div>
                  <div data-id="element-3908">
                    <span className="font-semibold" data-id="element-3909">Feeding:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.feeding}
                  </div>
                </div>
                <div className="text-sm" data-id="element-3910">
                  <span className="font-semibold" data-id="element-3911">Additional Notes:</span>
                  <p className="whitespace-pre-wrap mt-1" data-id="element-3912">
                    {selectedFile.postNatalSixWeeks?.infant.additionalNotes}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>}
    </div>;
};